'use client';
import { captureMessage } from '@sentry/nextjs';
import Cookies from 'js-cookie';
import { usePathname } from 'next/navigation';
import { createContext, PropsWithChildren, useEffect, useState } from 'react';

import { SERVER_DATA_HEADERS } from '@/constant/serverData';
import { useServerDataCtx } from '@/hooks/ctx';
import { useAnchor } from '@/hooks/navigation';
import { CookiesTOCState } from '@/types/server';
import { TOCContextData } from '@/types/ssr';
import { T } from '@/utils/common';
import { mergeRight } from '@/utils/common';
import { isClientSide } from '@/utils/next';

const getLastActiveTOCStateClientSide = (): CookiesTOCState => {
  try {
    return JSON.parse(Cookies.get(SERVER_DATA_HEADERS.lastActiveTOCsState) || '{}');
  } catch (err) {
    captureMessage(`failed parsed active toc element fron cookies on client side: ${err}`, { level: 'error' });
    return {};
  }
};

export const TOCContext = createContext<TOCContextData | null>(null);

export function TOCContextProvider({ children }: PropsWithChildren) {
  const { headers } = useServerDataCtx();
  const pathname = usePathname();
  const { anchor } = useAnchor();
  const cookiesTOCState = isClientSide() ? getLastActiveTOCStateClientSide() : headers.lastActiveTOCsState;
  // const [activeTOCElement, setActiveTOCElement] = useState(cookiesTOCState[pathname]);
  const [activeTOCElement, setActiveTOCElement] = useState(anchor);

  useEffect(() => {
    if (anchor) {
      setActiveTOCElement(anchor);
    }
  }, [anchor]);

  const handleChangeActiveTOCElement = (element: string, updateCookiesPredicate = T) => {
    setActiveTOCElement(element);
    try {
      if (updateCookiesPredicate()) {
        return Cookies.set(
          SERVER_DATA_HEADERS.lastActiveTOCsState,
          JSON.stringify(mergeRight(cookiesTOCState, { [pathname]: element })),
        );
      }
    } catch (err) {
      captureMessage(`faield to parse TOC form cookies, ${err}`, { level: 'error' });
      return;
    }
  };

  return (
    <TOCContext.Provider value={{ activeTOCElement, handleChangeActiveTOCElement }}>{children}</TOCContext.Provider>
  );
}
